import * as Immutable from 'immutable';
import {getLDClient} from 'ld-client';
import {createReducer} from 'redux-store';
import {getDefaultSelectedCaptureInputs, getDefaultSelectedCaptureInputs_SCLD17956} from 'utils/recording-utils';
import * as LiveSessionActions from '../constants/live-session-action-types';

import type {List, MapOf} from 'immutable';

export type AudioParameters = MapOf<{
    bitrate: number;
    channels: number;
    samplingFrequency: number;
}>;

export type SpriteParameters = MapOf<{
    frameDuration: number;
    frameHeight: number;
    frameWidth: number;
    framesPerSprite: number;
    quality: number;
}>;

export type VideoParameters = MapOf<{
    bitrate: number;
    frameRate: number;
    resolution: MapOf<{
        height: number;
        width: number;
    }>;
}>;

export type Channel = MapOf<{
    // This type is a CaptureInput which contains `channels: List<Channel>`. This causes a recorsive type reference that
    // causes type errors in some cases. To get proper type validation `captureInputId` might need to have the type
    // for `channels` modified to avoid this issue.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    captureInputId?: any;
    channelId: string;
    configuration: ChannelConfiguration;
    errors: List<
        MapOf<{
            message: string;
            payload: unknown;
            timestamp: string;
            type: string;
        }>
    >;
    localRtmp?: List<
        MapOf<{
            rtmpServerPath: string;
            rtmpStreamName: string;
            rtmpStreamToken: string;
        }>
    >;
    ptz?: unknown;
    status: string;
}>;

export type ChannelConfiguration = MapOf<{
    audioDspParameters: unknown;
    audioParameters: AudioParameters;
    displayName: string;
    spriteParameters: SpriteParameters;
    streamName: string;
    videoParameters: VideoParameters;
}>;

export type PtzConfiguration = MapOf<{
    absoluteRange: MapOf<{
        start: number;
        step: number;
        stop: number;
    }>;
    currentAbsoluteValue: number;
    supportAbsolute: boolean;
    supportContinuous: boolean;
}>;

export type Ptz = MapOf<{
    pan: PtzConfiguration;
    supportMoveToLocation: boolean;
    supportMoveToPreset: boolean;
    presetNames: List<string>;
    tilt: PtzConfiguration;
    zoom: PtzConfiguration;
}>;

export type CaptureInput = MapOf<{
    audioCapabilities: List<AudioParameters>;
    audioStatus: string;
    captureInputId: string;
    channels: List<Channel>;
    defaultChannelConfigurations: MapOf<{
        high: ChannelConfiguration;
        normal: ChannelConfiguration;
    }>;
    errors: List<unknown>;
    ptz: Ptz;
    sort: number;
    videoCapabilities: List<VideoParameters>;
    videoStatus: string;
}>;

export type SystemInfo = MapOf<{
    applicationPlatform: string;
    captureNodeVersion: string;
    hostNames: List<string>;
    macAddresses: List<string>;
    machineInfo: MapOf<{
        manufacturer: string;
        model: string;
        name: string;
        type: string;
    }>;
    machineName: string;
    operatingSystemInfo: MapOf<{
        displayVersion: unknown;
        name: string;
        platform: string;
        type: string;
        updateBuildRevision: string;
        version: string;
    }>;
    operatingSystemPlatform: string;
    operatingSystemVersion: string;
    operatingSystemType: string;
    recorderVersion: string;
    recordingsBytesAvailable: number;
    remoteAddress: string;
    simMergeVersion: string;
    simulatorDataBytesAvailable: number;
    spritesBytesAvailable: number;
}>;

export type CaptureNode = MapOf<{
    captureInputs: List<CaptureInput>;
    captureNodeId: string;
    errors: List<unknown>;
    recorderConfig: MapOf<{
        maxInputsAllowed: number;
        usingWebcams: boolean;
    }>;
    recorderErrors: List<unknown>;
    simulators: List<unknown>;
    status: string;
    systemInfo: SystemInfo;
    timeStamp: string;
    updaterPlatform: string;
    updaterStatus: string;
    updaterVersion: string;
}>;

export type IpVideoFeed = MapOf<{
    createdTs: string;
    clientId: string;
    ipAddress: string;
    ipAudioFeed: unknown;
    ipAudioFeedId: string;
    ipVideoFeedId: string;
    ipFeedTypeId: string;
    name: string;
    password: string;
    port: unknown;
    requiresSsl: boolean;
    rtspUrl: unknown;
    sourceNumber: unknown;
    updatedTs: string;
    username: string;
    xref_Locations_IpVideoFeeds: MapOf<{
        ipVideoFeedId: string;
        locationId: string;
        sort: number;
    }>;
    captureInputId?: string;
}>;

export type Location = MapOf<{
    active: boolean;
    captureHostId: string;
    clientId: string;
    inUse: boolean;
    ipVideoFeeds: List<IpVideoFeed>;
    locationId: string;
    name: string;
    sort: number;
}>;

export type Session = MapOf<{
    sessionId: string;
}>;

export type CaptureHost = MapOf<{
    active: boolean;
    captureHostId: string;
    captureNode: CaptureNode;
    captureNodeId: string;
    clientId: string;
    directStreamToken: string;
    inUse: boolean;
    location: Location;
    name: string;
    sessions: List<Session>;
    sortOrder: number;
    webRtcSessionId: string;
}>;

export type LiveSessionStore = {
    captureHost: CaptureHost;
    selectedCaptureInputs: List<CaptureInput>;
    selectedPeerIds: List<unknown>;
    startSessionInProgress: boolean;
    title: string;
};

const initialState = (): LiveSessionStore => {
    return {
        // @ts-expect-error TS2322: Type Map<unknown, unknown> is not assignable to type CaptureHost
        captureHost: Immutable.Map(),
        selectedCaptureInputs: Immutable.List(),
        selectedPeerIds: Immutable.List(),
        startSessionInProgress: false,
        title: '',
    };
};

/**
 * @todo SCLD-17956
 */
const getSelectedCaptureInputs = (data) => {
    const enableLbrFeedReordering = getLDClient().variation('enable-lbr-feed-reordering', false);

    try {
        return enableLbrFeedReordering
            ? getDefaultSelectedCaptureInputs_SCLD17956(data)
            : getDefaultSelectedCaptureInputs(data);
    } catch (error) {
        console.error(error);
        return getDefaultSelectedCaptureInputs(data);
    }
};

const actionsMap = {
    [LiveSessionActions.LIVE_DEVICE_SUCCESS]: (state, action) => {
        const selectedCaptureInputs = getSelectedCaptureInputs(action.data);
        const location = action.data.get('location');
        const simulator = action.data.get('simulator');

        let captureHost = action.data;
        const captureInputsWithPtzInfoOnChannel = captureHost
            .getIn(['captureNode', 'captureInputs'], Immutable.List())
            .map((ci) =>
                ci.set(
                    'channels',
                    ci.get('channels').map((c) => c.set('ptz', ci.get('ptz'))),
                ),
            );

        captureHost = captureHost.setIn(['captureNode', 'captureInputs'], captureInputsWithPtzInfoOnChannel);

        return {
            captureHost,
            location,
            selectedCaptureInputs,
            simulator,
        };
    },
    [LiveSessionActions.LIVE_CAPTURE_INPUTS_REFRESH_SUCCESS]: (state, action) => {
        const selectedCaptureInputs = getSelectedCaptureInputs(action.data);
        return {
            selectedCaptureInputs,
        };
    },
    [LiveSessionActions.LIVE_START_REQUEST]: (state, action) => {
        return {
            startSessionInProgress: action.startSessionInProgress,
        };
    },
    [LiveSessionActions.LIVE_SESSION_UPDATE_TITLE]: (state, action) => {
        return {
            title: action.title,
        };
    },
    [LiveSessionActions.LIVE_SESSION_SELECTED_CAPTURE_INPUTS_UPDATE]: (state, action) => {
        return {
            selectedCaptureInputs: action.data,
        };
    },
    [LiveSessionActions.LIVE_SESSION_UPDATE_SELECTED_PEERS]: (state, action) => {
        return {
            selectedPeerIds: action.selectedPeerIds,
        };
    },
    [LiveSessionActions.DISPOSE_LIVE_SESSION_DETAIL_STORE]: () => {
        return initialState();
    },
};

export default createReducer<LiveSessionStore>(initialState(), actionsMap);
